/**
 * GA4 event helpers. Queues hits until gtag exists and waits briefly
 * before Stripe redirects so begin_checkout is not dropped on unload.
 * Events are skipped until the user accepts analytics cookies.
 *
 * purchase fires on any Stripe success return (dashboard or /pricing),
 * not only the pricing page.
 */

import { CONSENT_REJECTED, getCookieConsent, hasAnalyticsConsent } from './cookieConsent';

const CHECKOUT_WAIT_MS = 2000;
const CHECKOUT_KIND_KEY = 'clarcv_checkout_kind';
const STRIPE_RETURN_KEYS = ['success', 'session_id', 'kind', 'canceled', 'billing'];

export const CHECKOUT_KIND_PRO = 'pro_intro';
export const CHECKOUT_KIND_ONESHOT = 'oneshot_pdf';
export const CHECKOUT_KIND_PDF_OFFER = 'oneshot_pdf_offer';
export const CHECKOUT_KIND_ATS = 'oneshot_ats';

const CHECKOUT_OFFERS = {
  [CHECKOUT_KIND_PRO]: {
    value: 1.95,
    item_id: 'clarcv_pro_intro',
    item_name: 'ClarCV Pro 3 days',
  },
  [CHECKOUT_KIND_ONESHOT]: {
    value: 2.95,
    item_id: 'clarcv_oneshot_pdf',
    item_name: 'ClarCV PDF',
  },
  [CHECKOUT_KIND_PDF_OFFER]: {
    value: 1.95,
    item_id: 'clarcv_pdf_offer_195',
    item_name: 'ClarCV PDF offer',
  },
  [CHECKOUT_KIND_ATS]: {
    value: 0.99,
    item_id: 'clarcv_oneshot_ats',
    item_name: 'ClarCV ATS report',
  },
};

const isCheckoutKind = (kind) =>
  kind === CHECKOUT_KIND_PRO ||
  kind === CHECKOUT_KIND_ONESHOT ||
  kind === CHECKOUT_KIND_PDF_OFFER ||
  kind === CHECKOUT_KIND_ATS;

const gaId = () => (process.env.REACT_APP_GA_MEASUREMENT_ID || '').trim();

const settleOnce = (resolve) => {
  let done = false;
  return () => {
    if (done) {
      return;
    }
    done = true;
    resolve();
  };
};

/**
 * Catalog row for a Checkout kind.
 *
 * @param {string} [kind]
 * @returns {{ value: number, item_id: string, item_name: string }}
 */
export const checkoutOffer = (kind) =>
  CHECKOUT_OFFERS[kind] || CHECKOUT_OFFERS[CHECKOUT_KIND_PRO];

/**
 * Persist the Checkout kind so purchase can resolve value after Stripe.
 *
 * @param {string} kind
 */
export const rememberCheckoutKind = (kind) => {
  if (!isCheckoutKind(kind)) {
    return;
  }
  try {
    window.sessionStorage.setItem(CHECKOUT_KIND_KEY, kind);
  } catch {
    /* private mode */
  }
};

/**
 * Kind from the success URL, then sessionStorage, then Pro intro.
 *
 * @param {string} [fromQuery]
 * @returns {string}
 */
export const readCheckoutKind = (fromQuery) => {
  if (isCheckoutKind(fromQuery)) {
    return fromQuery;
  }
  try {
    const stored = window.sessionStorage.getItem(CHECKOUT_KIND_KEY);
    if (isCheckoutKind(stored)) {
      return stored;
    }
  } catch {
    /* private mode */
  }
  return CHECKOUT_KIND_PRO;
};

const clearCheckoutKind = () => {
  try {
    window.sessionStorage.removeItem(CHECKOUT_KIND_KEY);
  } catch {
    /* private mode */
  }
};

/**
 * Parse Stripe return query flags.
 *
 * @param {string} [search] - location.search
 * @returns {{ success: boolean, canceled: boolean, billingReturn: boolean, sessionId: string, kind: string }}
 */
export const parseStripeReturn = (search) => {
  const params = new URLSearchParams((search || '').replace(/^\?/, ''));
  return {
    success: params.get('success') === '1',
    canceled: params.get('canceled') === '1',
    billingReturn: params.get('billing') === 'return',
    sessionId: params.get('session_id') || '',
    kind: params.get('kind') || '',
  };
};

/**
 * Drop Stripe return keys; keep any other query params.
 *
 * @param {string} [search]
 * @returns {string}
 */
export const stripStripeReturnSearch = (search) => {
  const raw = (search || '').replace(/^\?/, '');
  if (!raw) {
    return '';
  }
  const params = new URLSearchParams(raw);
  STRIPE_RETURN_KEYS.forEach((key) => {
    params.delete(key);
  });
  const serialized = params.toString();
  return serialized ? `?${serialized}` : '';
};

const GA_QUEUE_KEY = 'clarcv_ga_queue';
const GA_QUEUE_MAX = 25;

const readGaQueue = () => {
  if (typeof window === 'undefined') {
    return [];
  }
  try {
    const raw = window.sessionStorage.getItem(GA_QUEUE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writeGaQueue = (items) => {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.sessionStorage.setItem(
      GA_QUEUE_KEY,
      JSON.stringify(items.slice(-GA_QUEUE_MAX))
    );
  } catch {
    /* private mode */
  }
};

const enqueueGaEvent = (eventName, params) => {
  if (getCookieConsent() === CONSENT_REJECTED) {
    return;
  }
  writeGaQueue([...readGaQueue(), { eventName, params }]);
};

const dispatchGaEvent = (eventName, params, finish) => {
  if (typeof window.gtag !== 'function') {
    window.setTimeout(finish, CHECKOUT_WAIT_MS);
    return;
  }
  window.gtag('event', eventName, {
    ...params,
    transport_type: 'beacon',
    event_callback: finish,
    event_timeout: CHECKOUT_WAIT_MS,
  });
  window.setTimeout(finish, CHECKOUT_WAIT_MS);
};

/**
 * Send events that fired before gtag loaded (sign_up, cv_upload, …).
 * Call after the GA Consent Mode shell is bootstrapped.
 */
export const flushQueuedGaEvents = () => {
  if (typeof window === 'undefined' || !gaId()) {
    return;
  }
  if (getCookieConsent() === CONSENT_REJECTED) {
    writeGaQueue([]);
    return;
  }
  const queued = readGaQueue();
  writeGaQueue([]);
  queued.forEach((item) => {
    if (!item || !item.eventName) {
      return;
    }
    dispatchGaEvent(item.eventName, item.params || {}, () => {});
  });
};

/**
 * Send a GA4 event via gtag when available.
 * Before gtag loads, queues the hit. Rejected cookies skip entirely.
 * With Consent Mode default denied, events still send cookieless until Accept.
 *
 * @param {string} eventName - GA4 event name (e.g. sign_up, begin_checkout)
 * @param {Object} [params] - Event parameters
 * @returns {Promise<void>}
 */
export const trackGaEvent = (eventName, params = {}) =>
  new Promise((resolve) => {
    const finish = settleOnce(resolve);
    if (typeof window === 'undefined' || !gaId()) {
      finish();
      return;
    }
    if (getCookieConsent() === CONSENT_REJECTED) {
      finish();
      return;
    }
    if (typeof window.gtag !== 'function') {
      enqueueGaEvent(eventName, params);
      finish();
      return;
    }
    dispatchGaEvent(eventName, params, finish);
  });

/**
 * New account created (register succeeded; may still need email activation).
 *
 * @param {string} [method]
 * @returns {Promise<void>}
 */
export const trackSignUp = (method = 'email') =>
  trackGaEvent('sign_up', { method });

/**
 * User starts Stripe Checkout (Pro intro or one-shot PDF).
 *
 * @param {{ kind?: string, currency?: string }} [opts]
 * @returns {Promise<void>}
 */
export const trackBeginCheckout = ({
  kind = CHECKOUT_KIND_PRO,
  currency = 'EUR',
} = {}) => {
  const resolved = readCheckoutKind(kind);
  rememberCheckoutKind(resolved);
  const offer = checkoutOffer(resolved);
  return trackGaEvent('begin_checkout', {
    currency,
    value: offer.value,
    items: [
      {
        item_id: offer.item_id,
        item_name: offer.item_name,
        price: offer.value,
        quantity: 1,
      },
    ],
  });
};

/**
 * Stripe Checkout completed (return URL ?success=1 on any in-app path).
 *
 * @param {{ kind?: string, currency?: string, transactionId?: string }} [opts]
 * @returns {Promise<void>}
 */
export const trackPurchase = ({
  kind,
  currency = 'EUR',
  transactionId,
} = {}) => {
  const resolved = readCheckoutKind(kind);
  const offer = checkoutOffer(resolved);
  const params = {
    currency,
    value: offer.value,
    items: [
      {
        item_id: offer.item_id,
        item_name: offer.item_name,
        price: offer.value,
        quantity: 1,
      },
    ],
  };
  if (transactionId) {
    params.transaction_id = transactionId;
  }
  return trackGaEvent('purchase', params);
};

/**
 * Fire purchase once per Checkout session_id (survives React Strict Mode).
 *
 * @param {{ sessionId?: string, kind?: string }} [opts]
 * @returns {Promise<void>}
 */
export const trackPurchaseOnce = ({ sessionId, kind } = {}) => {
  const purchaseKey = `clarcv_ga_purchase_${sessionId || 'ok'}`;
  let alreadySent = false;
  try {
    alreadySent = Boolean(window.sessionStorage.getItem(purchaseKey));
    if (!alreadySent) {
      window.sessionStorage.setItem(purchaseKey, '1');
    }
  } catch {
    alreadySent = false;
  }
  if (alreadySent) {
    return Promise.resolve();
  }
  const sent = trackPurchase({
    kind,
    transactionId: sessionId || undefined,
  });
  clearCheckoutKind();
  return sent;
};

/**
 * User accepted a CV file on an upload flow (call after a successful POST).
 *
 * @param {{ source?: string }} [opts]
 * @returns {Promise<void>}
 */
export const trackCvUpload = ({ source = 'quick_edit' } = {}) =>
  trackGaEvent('cv_upload', { source });

/**
 * Extract / rewrite succeeded and the studio opened.
 *
 * @returns {Promise<void>}
 */
export const trackCvExtractOk = () => trackGaEvent('cv_extract_ok');

/**
 * ATS report finished successfully (poll SUCCESS).
 *
 * @returns {Promise<void>}
 */
export const trackAtsAnalysisOk = () => trackGaEvent('ats_analysis_ok');

/**
 * User picked ATS report on /dashboard/from-file.
 *
 * @returns {Promise<void>}
 */
export const trackChoiceAnalyze = () => trackGaEvent('choice_analyze');

/**
 * User picked AI rewrite on /dashboard/from-file.
 *
 * @returns {Promise<void>}
 */
export const trackChoiceRebuild = () => trackGaEvent('choice_rebuild');

/**
 * User picked rewrite-same-CV (keep look) on /dashboard/from-file.
 *
 * @returns {Promise<void>}
 */
export const trackChoiceKeepLook = () => trackGaEvent('choice_keep_look');

/**
 * User clicked “Prepare my CV with AI” on an ATS report.
 *
 * @param {{ hasJob?: boolean, reuseFile?: boolean }} [opts]
 * @returns {Promise<void>}
 */
export const trackAtsPrepareAiClick = ({ hasJob = false, reuseFile = false } = {}) =>
  trackGaEvent('ats_prepare_ai_click', {
    has_job: hasJob,
    reuse_file: reuseFile,
  });

/**
 * Autostart from ATS (?autostart=1) did not reach the editor.
 *
 * @param {{ reason?: string }} [opts]
 * @returns {Promise<void>}
 */
export const trackQuickEditAutostartFail = ({ reason = 'unknown' } = {}) =>
  trackGaEvent('quick_edit_autostart_fail', { reason });

/**
 * Studio applied a look without the user picking one.
 *
 * @param {{ id?: string|number, name?: string }} [template]
 * @returns {Promise<void>}
 */
export const trackTemplateAutoApplied = (template = {}) =>
  trackGaEvent('template_auto_applied', {
    template_id: template.id,
    template_name: template.name,
  });

/**
 * User picked a template from the rail or gallery.
 *
 * @param {{ id?: string|number, name?: string }} [template]
 * @returns {Promise<void>}
 */
export const trackTemplateSelected = (template = {}) =>
  trackGaEvent('template_selected', {
    template_id: template.id,
    template_name: template.name,
  });

/**
 * Live A4 HTML preview rendered for a template (once per look).
 *
 * @param {{ id?: string|number, name?: string }} [template]
 * @returns {Promise<void>}
 */
export const trackCvPreview = (template = {}) =>
  trackGaEvent('cv_preview', {
    template_id: template.id,
    template_name: template.name,
  });

/**
 * PDF (or other format) download started successfully.
 *
 * @param {{ format?: string, template?: { id?: string|number, name?: string } }} [opts]
 * @returns {Promise<void>}
 */
export const trackCvDownload = ({ format = 'pdf', template = {} } = {}) =>
  trackGaEvent('cv_download', {
    format,
    template_id: template.id,
    template_name: template.name,
  });

/**
 * User opened the studio design-prompt field.
 *
 * @returns {Promise<void>}
 */
export const trackTemplatePromptOpen = () => trackGaEvent('template_prompt_open');

/**
 * AI look generated from a prompt and shown on the A4.
 *
 * @returns {Promise<void>}
 */
export const trackTemplatePromptGenerated = () =>
  trackGaEvent('template_prompt_generated');
